// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { BankAccount } from '@client/banks/types/account';
import type { BankUploadForm } from '@client/banks/types/create-bank-form';
import type { BankDebt } from '@client/banks/types/debt';
import type {
  BankUploadListEditor,
  BankUploadListItemMap,
  BankUploadListSection,
} from '@client/banks/types/bank-list-editor';
import type { BankPosition } from '@client/banks/types/position';
import type { BankTransaction } from '@client/banks/types/transaction';
import { useBankCurrencyOptions } from '@client/banks/hooks/use-bank-currency-options';
import { useBankUploadSectionMeta } from '@client/banks/hooks/use-bank-upload-section-meta';
import { bankInstitutionSchema } from '@client/banks/types/institution';
import { buildBankUploadFormFromAccountData } from '@client/banks/utils/build-bank-upload-form-from-account-data';
import {
  formatAccountIbanReference,
  hasLinkableAccountIban,
} from '@client/banks/utils/account-reference';
import { getBankUploadListItems } from '@client/banks/utils/bank-list-editor';
import {
  isMeaningfulBankUploadDraft,
  selectBankUploadDraft,
} from '@shared/data-access/slices/clients/clients-selectors';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useDrawer } from '@shared/ui-kit/hooks/use-drawer';
import { createDataProvider } from '@shared/ui-kit/providers/data-provider/create-data-provider';
import produce from 'immer';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { clientsSlice } from '@shared/data-access/slices/clients';
import { buildBulkUploadAccountData } from '../utils';

/** OptionsSelector value for “no linked liquidity account”. */
export const BANK_UPLOAD_UNLINKED_ACCOUNT_VALUE = '';

const initialFormData: BankUploadForm = {
  institution: {
    name: '',
    logoUrl: '',
  },
  accounts: [],
  positions: [],
  debts: [],
  transactions: [],
};

export type BankUploadProviderProps = {
  externalConnectContent?: ReactNode;
};

export const useBankUploadFactory = ({
  externalConnectContent = null,
}: BankUploadProviderProps = {}) => {
  const { t } = useTranslation();
  const { reset: resetSteps } = useStepsData();

  const { markSectionCompleted } = useBankUploadSectionMeta();

  const bankUploadDraft = useSelector(selectBankUploadDraft);

  const [formData, setFormData] = useState<BankUploadForm>(initialFormData);

  const [listEditor, setListEditor] = useState<BankUploadListEditor | null>(null);

  const currencyOptions = useBankCurrencyOptions();

  const dispatch = useDispatch();

  const [createAdvisoryAccountsCitec, { isLoading: isSubmittingBankUpload }] =
    advisoryApi.useCreateAdvisoryAccountsCitecMutation();

  const syncFormData = useCallback(
    (data: BankUploadForm) => {
      const payload = buildBulkUploadAccountData(data);

      if (!isMeaningfulBankUploadDraft(payload)) {
        dispatch(clientsSlice.actions.clearBankUploadDraft());

        return;
      }

      dispatch(clientsSlice.actions.setBankUploadDraft(payload));
    },
    [dispatch],
  );

  const applyFormData = useCallback(
    (nextFormData: BankUploadForm) => {
      setFormData(nextFormData);

      setListEditor(null);

      syncFormData(nextFormData);

      const completedSections = [
        {
          section: 'institution',
          completed: bankInstitutionSchema.safeParse(nextFormData.institution)
            .success,
        },
        {
          section: 'accounts',
          completed: nextFormData.accounts.length > 0,
        },
        {
          section: 'positions',
          completed: (nextFormData.positions?.length ?? 0) > 0,
        },
        {
          section: 'debts',
          completed: (nextFormData.debts?.length ?? 0) > 0,
        },
        {
          section: 'transactions',
          completed: (nextFormData.transactions?.length ?? 0) > 0,
        },
      ] as const;

      completedSections.forEach(section => {
        if (section.completed) {
          markSectionCompleted(section.section);
        }
      });
    },
    [markSectionCompleted, syncFormData],
  );

  const hydrateFormData = useCallback(() => {
    if (bankUploadDraft === null || !isMeaningfulBankUploadDraft(bankUploadDraft)) {
      setFormData(initialFormData);
      setListEditor(null);

      if (bankUploadDraft !== null) {
        dispatch(clientsSlice.actions.clearBankUploadDraft());
      }

      return;
    }

    applyFormData(buildBankUploadFormFromAccountData(bankUploadDraft));
  }, [applyFormData, bankUploadDraft, dispatch]);

  const resetFormData = useCallback(() => {
    setFormData(initialFormData);

    setListEditor(null);

    dispatch(clientsSlice.actions.clearBankUploadDraft());
  }, [dispatch]);

  const [DrawerRenderer, drawerControls] = useDrawer({
    beforeOpen: () => {
      resetSteps();

      hydrateFormData();
    },
  });

  const saveFormData = useCallback(
    <K extends keyof BankUploadForm>(section: K, data: BankUploadForm[K]) => {
      const nextFormData = produce(formData, draft => {
        draft[section] = data;
      });

      setFormData(nextFormData);

      syncFormData(nextFormData);
    },
    [formData, syncFormData],
  );

  /** Replace the whole draft in one write (multi-section updates). */
  const replaceFormData = useCallback(
    (nextFormData: BankUploadForm) => {
      setFormData(nextFormData);

      syncFormData(nextFormData);
    },
    [syncFormData],
  );

  const getFormData = useCallback(
    <K extends keyof BankUploadForm>(section: K): BankUploadForm[K] => {
      return formData[section];
    },
    [formData],
  );

  const getAllFormData = useCallback(() => formData, [formData]);

  const linkableAccounts = useMemo(
    () => formData.accounts.filter(account => hasLinkableAccountIban(account.iban)),
    [formData.accounts],
  );

  const accountIbanOptions = useMemo(
    () => [
      {
        value: BANK_UPLOAD_UNLINKED_ACCOUNT_VALUE,
        label: t('aggregator.create-bank.linked-account-none', {
          ns: 'aggregator',
        }),
      },
      ...linkableAccounts.map(account => ({
        value: account.iban,
        label: `${account.name} · ${formatAccountIbanReference(account.iban)}`,
      })),
    ],
    [linkableAccounts, t],
  );

  const openListEditor = useCallback(
    (section: BankUploadListSection, index?: number) => {
      const items = getBankUploadListItems(formData, section);

      setListEditor({ section, index: index ?? items.length });
    },
    [formData],
  );

  const closeListEditor = useCallback(() => {
    setListEditor(null);
  }, []);

  const commitListEditor = useCallback(
    <S extends BankUploadListSection>(item: BankUploadListItemMap[S]) => {
      if (!listEditor) {
        return;
      }

      const { section, index } = listEditor;

      switch (section) {
        case 'accounts': {
          const nextItems = produce(formData.accounts, draft => {
            const account = item as BankAccount;

            if (index < draft.length) {
              draft[index] = account;
            } else {
              draft.unshift(account);
            }
          });

          saveFormData('accounts', nextItems);

          break;
        }
        case 'positions': {
          const nextItems = produce(formData.positions ?? [], draft => {
            const position = item as BankPosition;

            if (index < draft.length) {
              draft[index] = position;
            } else {
              draft.unshift(position);
            }
          });

          saveFormData('positions', nextItems);

          break;
        }
        case 'debts': {
          const nextItems = produce(formData.debts ?? [], draft => {
            const debt = item as BankDebt;

            if (index < draft.length) {
              draft[index] = debt;
            } else {
              draft.unshift(debt);
            }
          });

          saveFormData('debts', nextItems);

          break;
        }
        case 'transactions': {
          const nextItems = produce(formData.transactions ?? [], draft => {
            const transaction = item as BankTransaction;

            if (index < draft.length) {
              draft[index] = transaction;
            } else {
              draft.unshift(transaction);
            }
          });

          saveFormData('transactions', nextItems);

          break;
        }
      }

      setListEditor(null);
    },
    [formData, listEditor, saveFormData],
  );

  const submitBankUpload = useCallback(async () => {
    await createAdvisoryAccountsCitec(
      buildBulkUploadAccountData(formData),
    ).unwrap();
  }, [createAdvisoryAccountsCitec, formData]);

  return {
    DrawerRenderer,
    ...drawerControls,
    formData,
    saveFormData,
    replaceFormData,
    getFormData,
    getAllFormData,
    resetFormData,
    listEditor,
    openListEditor,
    closeListEditor,
    commitListEditor,
    currencyOptions,
    accountIbanOptions,
    submitBankUpload,
    isSubmittingBankUpload,
    externalConnectContent,
  };
};

export const {
  useBankUploadData,
  BankUploadProvider,
  BankUploadConsumer,
  BankUploadDataContext,
  withBankUpload,
} = createDataProvider({
  name: 'BankUpload',
  factory: useBankUploadFactory,
});
